// Portfolio Backtesting Component
import React, { useState, useEffect } from 'react';
import './PortfolioBacktest.css';
import PortfolioResults from './PortfolioResults';

const PortfolioBacktest = () => {
  // State management
  const [stockLists, setStockLists] = useState({});
  const [selectedList, setSelectedList] = useState('nifty50');
  const [availableStocks, setAvailableStocks] = useState([]);
  const [selectedStocks, setSelectedStocks] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSector, setSelectedSector] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState(null);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  // Strategy parameters
  const [strategyParams, setStrategyParams] = useState({
    start_date: '2023-01-01',
    end_date: '2023-12-31',
    entry_conditions: 'CLOSE > SMA(20)',
    exit_conditions: 'CLOSE < SMA(20)',
    initial_capital: 100000,
    position_size: 1000
  });

  // Load stock lists on component mount
  useEffect(() => {
    loadStockLists();
  }, []);

  // Load stocks when selected list changes
  useEffect(() => {
    if (selectedList && stockLists[selectedList]) {
      setAvailableStocks(stockLists[selectedList].stocks);
      setSelectedStocks([]); // Clear selected stocks when changing lists
      setSearchTerm('');
      setSelectedSector('');
    }
  }, [selectedList, stockLists]);

  const loadStockLists = async () => {
    try {
      setIsLoading(true);
      const response = await fetch('http://localhost:8000/api/portfolio/stock-lists');
      const data = await response.json();
      
      if (data.success) {
        setStockLists(data.data);
        // Set first list as default
        const firstListKey = Object.keys(data.data)[0];
        if (firstListKey) {
          setSelectedList(firstListKey);
        }
      } else {
        setError('Failed to load stock lists');
      }
    } catch (err) {
      setError('Error loading stock lists: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Filter stocks based on search term and sector
  const filteredStocks = availableStocks.filter(stock => {
    const matchesSearch = !searchTerm || 
      stock.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
      stock.name.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesSector = !selectedSector || stock.sector === selectedSector;
    
    return matchesSearch && matchesSector;
  });

  // Get unique sectors for filtering
  const uniqueSectors = [...new Set(availableStocks.map(stock => stock.sector))].sort();

  const handleStockSelect = (stock) => {
    const isSelected = selectedStocks.some(s => s.symbol === stock.symbol);
    
    if (isSelected) {
      // Remove stock
      setSelectedStocks(selectedStocks.filter(s => s.symbol !== stock.symbol));
    } else {
      // Add stock (max 10)
      if (selectedStocks.length < 10) {
        setSelectedStocks([...selectedStocks, stock]);
      } else {
        alert('Maximum 10 stocks allowed per portfolio');
      }
    }
  };

  const handleRemoveStock = (stockToRemove) => {
    setSelectedStocks(selectedStocks.filter(s => s.symbol !== stockToRemove.symbol));
  };

  const handleStrategyParamChange = (param, value) => {
    setStrategyParams({
      ...strategyParams,
      [param]: value
    });
  };

  const startPortfolioBacktest = async () => {
    if (selectedStocks.length === 0) {
      alert('Please select at least one stock');
      return;
    }

    try {
      setIsRunning(true);
      setError(null);
      setResults(null);
      
      // Prepare request
      const request = {
        symbols: selectedStocks.map(s => s.symbol),
        list_name: selectedList,
        ...strategyParams
      };

      // Start backtest
      const response = await fetch('http://localhost:8000/api/portfolio/backtest', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(request)
      });

      const data = await response.json();
      
      if (data.success) {
        const taskId = data.task_id;
        monitorProgress(taskId);
      } else {
        throw new Error(data.detail || 'Failed to start backtest');
      }

    } catch (err) {
      setError('Error starting backtest: ' + err.message);
      setIsRunning(false);
    }
  };

  const monitorProgress = async (taskId) => {
    const progressInterval = setInterval(async () => {
      try {
        const response = await fetch(`http://localhost:8000/api/portfolio/progress/${taskId}`);
        const data = await response.json();
        
        if (data.success) {
          const progressData = data.data;
          setProgress(progressData);
          
          if (progressData.status === 'COMPLETED') {
            clearInterval(progressInterval);
            setIsRunning(false);
            setResults(progressData.result);
          } else if (progressData.status === 'FAILED') {
            clearInterval(progressInterval);
            setIsRunning(false);
            setError(progressData.message);
          }
        }
      } catch (err) {
        console.error('Error monitoring progress:', err);
      }
    }, 2000); // Check every 2 seconds
  };

  const StockCard = ({ stock, isSelected, onSelect, disabled }) => (
    <div 
      className={`stock-card ${isSelected ? 'selected' : ''} ${disabled ? 'disabled' : ''}`}
      onClick={disabled ? null : onSelect}
    >
      <div className="stock-symbol">{stock.symbol}</div>
      <div className="stock-name">{stock.name}</div>
      <div className="stock-sector">{stock.sector}</div>
      {isSelected && <div className="selected-indicator">✓</div>}
    </div>
  );

  const ProgressBar = ({ progress }) => (
    <div className="progress-container">
      <div className="progress-header">
        <h3>Portfolio Backtest in Progress</h3>
        <div className="progress-stats">
          {progress.current}/{progress.total} stocks processed
        </div>
      </div>
      
      <div className="progress-bar">
        <div 
          className="progress-fill" 
          style={{width: `${(progress.current / progress.total) * 100}%`}}
        ></div>
      </div>
      
      <div className="progress-message">{progress.message}</div>
      
      {progress.processed_stocks && progress.processed_stocks.length > 0 && (
        <div className="processed-stocks">
          <h4>Processed Stocks:</h4>
          <div className="stock-chips">
            {progress.processed_stocks.map(stock => (
              <div key={stock.symbol} className="stock-chip success">
                {stock.symbol}: {stock.return > 0 ? '+' : ''}{stock.return.toFixed(2)}%
              </div>
            ))}
          </div>
        </div>
      )}
      
      {progress.failed_stocks && progress.failed_stocks.length > 0 && (
        <div className="failed-stocks">
          <h4>Failed Stocks:</h4>
          <div className="stock-chips">
            {progress.failed_stocks.map(stock => (
              <div key={stock.symbol} className="stock-chip error">
                {stock.symbol}: Error
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  if (isLoading) {
    return <div className="loading">Loading stock lists...</div>;
  }

  if (results) {
    // Show results component
    return <PortfolioResults results={results} onBack={() => setResults(null)} />;
  }

  return (
    <div className="portfolio-backtest">
      <div className="portfolio-header">
        <h1>Portfolio Backtesting</h1>
        <p>Test your strategy across multiple stocks simultaneously</p>
      </div>

      {error && (
        <div className="error-message">
          <strong>Error:</strong> {error}
          <button onClick={() => setError(null)} className="close-error">×</button>
        </div>
      )}

      {isRunning ? (
        <ProgressBar progress={progress} />
      ) : (
        <>
          {/* Stock List Selection */}
          <div className="section">
            <h2>1. Select Stock List</h2>
            <div className="list-selector">
              {Object.entries(stockLists).map(([key, list]) => (
                <label key={key} className="list-option">
                  <input
                    type="radio"
                    name="stockList"
                    value={key}
                    checked={selectedList === key}
                    onChange={(e) => setSelectedList(e.target.value)}
                  />
                  <div className="list-info">
                    <div className="list-name">{list.name}</div>
                    <div className="list-description">{list.description}</div>
                    <div className="list-count">{list.total_stocks} stocks</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Stock Selection */}
          <div className="section">
            <h2>2. Select Stocks (Max 10)</h2>
            
            {/* Search and Filter Controls */}
            <div className="search-controls">
              <input
                type="text"
                placeholder="Search stocks by name or symbol..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              
              <select
                value={selectedSector}
                onChange={(e) => setSelectedSector(e.target.value)}
                className="sector-filter"
              >
                <option value="">All Sectors</option>
                {uniqueSectors.map(sector => (
                  <option key={sector} value={sector}>{sector}</option>
                ))}
              </select>
            </div>

            {/* Selected Stocks Summary */}
            <div className="selected-summary">
              <h3>Selected Stocks ({selectedStocks.length}/10)</h3>
              <div className="selected-chips">
                {selectedStocks.map(stock => (
                  <div key={stock.symbol} className="stock-chip">
                    <span>{stock.symbol}</span>
                    <button 
                      onClick={() => handleRemoveStock(stock)}
                      className="remove-stock"
                    >×</button>
                  </div>
                ))}
              </div>
            </div>

            {/* Stock Grid */}
            <div className="stock-grid">
              {filteredStocks.map(stock => (
                <StockCard
                  key={stock.symbol}
                  stock={stock}
                  isSelected={selectedStocks.some(s => s.symbol === stock.symbol)}
                  onSelect={() => handleStockSelect(stock)}
                  disabled={selectedStocks.length >= 10 && !selectedStocks.some(s => s.symbol === stock.symbol)}
                />
              ))}
            </div>
            
            {filteredStocks.length === 0 && (
              <div className="no-stocks">No stocks match your search criteria</div>
            )}
          </div>

          {/* Strategy Configuration */}
          <div className="section">
            <h2>3. Configure Strategy</h2>
            <div className="strategy-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Start Date:</label>
                  <input
                    type="date"
                    value={strategyParams.start_date}
                    onChange={(e) => handleStrategyParamChange('start_date', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>End Date:</label>
                  <input
                    type="date"
                    value={strategyParams.end_date}
                    onChange={(e) => handleStrategyParamChange('end_date', e.target.value)}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Entry Conditions:</label>
                  <input
                    type="text"
                    value={strategyParams.entry_conditions}
                    onChange={(e) => handleStrategyParamChange('entry_conditions', e.target.value)}
                    placeholder="e.g., CLOSE > SMA(20)"
                  />
                </div>
                <div className="form-group">
                  <label>Exit Conditions:</label>
                  <input
                    type="text"
                    value={strategyParams.exit_conditions}
                    onChange={(e) => handleStrategyParamChange('exit_conditions', e.target.value)}
                    placeholder="e.g., CLOSE < SMA(20)"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Initial Capital:</label>
                  <input
                    type="number"
                    value={strategyParams.initial_capital}
                    onChange={(e) => handleStrategyParamChange('initial_capital', parseFloat(e.target.value))}
                  />
                </div>
                <div className="form-group">
                  <label>Position Size:</label>
                  <input
                    type="number"
                    value={strategyParams.position_size}
                    onChange={(e) => handleStrategyParamChange('position_size', parseFloat(e.target.value))}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Run Backtest Button (Work in Progress) */}
          <div className="section">
            <button 
              className="run-backtest-btn"
              disabled
              style={{ background: '#bdc3c7', cursor: 'not-allowed' }}
            >
              🚧 Work in Progress
            </button>
          </div>
        </>
      )}
    </div>
  );
};



export default PortfolioBacktest;